import { createTheme } from "@mui/material/styles";

/**
 * MUI's half of the palette.
 *
 * The colours themselves are custom properties in src/styles/palette.css. MUI's
 * theme is a JS object and Tailwind's utilities are build-time CSS, so a custom
 * property is the one shape both can read: this file only points MUI at the
 * properties, and the attribute selector in palette.css is what changes them.
 *
 * The scheme is the `data-theme` attribute on <html>. `InitColorSchemeScript`
 * writes it from the reader's stored mode before the first paint (gatsby-ssr.js)
 * and `ThemeProvider` keeps it in step afterwards, so MUI and Tailwind switch
 * together and a stored dark choice never flashes light. `colorSchemeSelector:
 * "media"` would ignore that choice, because the OS query cannot see it.
 *
 * `nativeColor` makes MUI derive its hover and overlay tints with color-mix()
 * instead of from a palette channel. A channel is an rgb triple it parses out of
 * the value at build time, which a `var()` does not have.
 */
const token = (name: string) => `var(${name})`;

/**
 * The three facts the two Gatsby roots and the before-paint script must agree
 * on. They live here because `theme.ts` is the one module both Gatsby files
 * already import; a second copy of the key would be a second thing to rename.
 */
export const colorScheme = {
  attribute: "data-theme",
  defaultMode: "system",
  modeStorageKey: "lm-color-scheme",
} as const;

const palette = {
  background: { default: token("--lm-surface"), paper: token("--lm-paper") },
  text: { primary: token("--lm-ink"), secondary: token("--lm-ink-muted") },
  divider: token("--lm-border"),
};

const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: colorScheme.attribute,
    nativeColor: true,
  },
  colorSchemes: { light: { palette }, dark: { palette } },
});

export default theme;
